import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/components/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Lock, AlertCircle } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function LoginPage() {
  const nav = useNavigate();
  const { authenticated, refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (authenticated) nav("/", { replace: true });
  }, [authenticated, nav]);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading || cooldown > 0) return;
    setLoading(true); setError("");
    try {
      await api.post("/auth/login", { password });
      await refresh();
      nav("/", { replace: true });
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      if (err?.response?.status === 429 || code === "TOO_MANY_ATTEMPTS") {
        setError("嘗試次數過多，請稍後再試");
        setCooldown(30);
      } else {
        setError(errorMessage(err, "密碼錯誤"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 py-8"
      style={{ background: "linear-gradient(135deg, #FEF3E8 0%, #F26A1F 100%)" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><BrandLogo size="lg" /></div>
        <div className="rounded-2xl bg-white border border-white/60 shadow-xl p-7">
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Lock className="h-5 w-5 text-[#F26A1F]" /> 登入
          </h1>
          <p className="mt-1 text-sm text-slate-500">輸入團隊共用密碼以存取 Lakeze 行銷數據中心</p>
          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">密碼</label>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="輸入共用密碼"
                className="bg-slate-50 border-slate-200 text-slate-900 font-mono"
                data-testid="login-password-input"
                autoFocus
              />
            </div>
            {error && (
              <div
                className="rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 flex items-center gap-2"
                data-testid="login-error"
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}{cooldown > 0 ? `（${cooldown}s）` : ""}</span>
              </div>
            )}
            <Button
              type="submit"
              disabled={loading || !password || cooldown > 0}
              className="w-full bg-[#F26A1F] hover:bg-[#D55B15] text-white disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {cooldown > 0 ? `請稍候 ${cooldown}s` : loading ? "登入中…" : "登入"}
            </Button>
          </form>
        </div>
        <div className="text-center text-[11px] text-white/80 mt-4">
          Lakeze 人客多 · 行銷數據中心
        </div>
      </div>
    </div>
  );
}
